import type { LeadProjectSummary, LeadSearchRow, OpportunityHypothesis } from "./types";

const ANGLE_LABELS: Record<string, string> = {
  next_phase: "Next phase",
  adjacent_system: "Adjacent system",
  integration: "Integration",
  data_and_ai: "Data & AI",
  security_and_resilience: "Security & resilience",
  regulatory_compliance: "Regulatory compliance",
  operations_and_managed_service: "Operations & managed service",
  legacy_decommission: "Legacy decommission",
  change_and_adoption: "Change & adoption",
  other: "Other",
};

export function angleLabel(angle: string | undefined): string {
  const key = String(angle || "").trim();
  if (!key) return "";
  return ANGLE_LABELS[key] || key.replace(/_/g, " ");
}

/** The operator-facing name of a lead's workflow state. */
export function leadStateLabel(lead: Pick<OpportunityHypothesis, "workflow_state" | "conclusion">): string {
  const state = lead.workflow_state;
  if (state === "dismissed") return lead.conclusion === "superseded" ? "Superseded" : "Dismissed";
  if (state === "completed") {
    return lead.conclusion === "linked_to_documented_initiative" ? "Linked to a project" : "Marked distinct";
  }
  return (
    {
      proposed: "New",
      eligible: "Queued for research",
      queued: "Queued for research",
      running: "Researching",
      cooldown: "Researched",
      deferred: "Deferred",
      exhausted: "Research exhausted",
    }[state] || state
  );
}

export function isModelLead(lead: Pick<OpportunityHypothesis, "generator">): boolean {
  return Boolean(lead.generator && lead.generator !== "rules");
}

export function isClosedLead(lead: Pick<OpportunityHypothesis, "workflow_state">): boolean {
  return lead.workflow_state === "dismissed";
}

export function shortModel(model: string | undefined): string {
  const id = String(model || "");
  return id.split("/").pop() || id;
}

export function shortDate(value: string | undefined): string {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function usd(value: number | undefined | null, digits = 2): string {
  const amount = Number(value || 0);
  if (amount > 0 && amount < 0.01) return "<$0.01";
  return `$${amount.toFixed(digits)}`;
}

/** Why the next Phase 1 run will not execute a lead query, in plain words. */
export function skipReasonLabel(row: Pick<LeadSearchRow, "skip_reason" | "not_before">): string {
  switch (row.skip_reason) {
    case "already_ran_for_target":
      return "Already produced results for this bank, so Phase 1 skips it";
    case "refresh_window":
      return `Not due again until ${shortDate(row.not_before) || row.not_before}`;
    case "attempt_limit":
      return "This lead's research used all its attempts";
    default:
      return "";
  }
}

/* ------------------------------------------------ lead coverage filters -- */

export type LeadFilter = "all" | "needs" | "none" | "stale" | "has" | "failed" | "found";

export const LEAD_FILTER_OPTIONS: { id: LeadFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "needs", label: "Needs leads (none yet or out of date)" },
  { id: "none", label: "No leads generated yet" },
  { id: "stale", label: "Leads out of date (new roadmap events)" },
  { id: "has", label: "Has current leads" },
  { id: "failed", label: "Last generation failed" },
  { id: "found", label: "Found via lead research (no leads of their own)" },
];

/** Found through another project's lead research: never a source of leads itself. */
export function isFoundViaLead(summary: LeadProjectSummary | undefined): boolean {
  return Boolean(summary?.found_via_lead);
}

/** "Has leads" means a completed model generation; rule-catalogue leads carry no keywords. */
export function hasGeneratedLeads(summary: LeadProjectSummary | undefined): boolean {
  return Boolean(summary?.generated_at);
}

export function projectNeedsLeads(summary: LeadProjectSummary | undefined): boolean {
  return !hasGeneratedLeads(summary) || Boolean(summary?.stale);
}

/** Unknown (summary not loaded yet) only passes "all", so a filter never over-selects. */
export function matchesLeadFilter(summary: LeadProjectSummary | undefined, filter: LeadFilter): boolean {
  if (filter === "all") return true;
  if (!summary) return false;
  if (filter === "found") return isFoundViaLead(summary);
  // A lead-found project never needs leads: that would be a second round.
  if (isFoundViaLead(summary) && filter !== "failed") return false;
  switch (filter) {
    case "needs":
      return projectNeedsLeads(summary);
    case "none":
      return !hasGeneratedLeads(summary);
    case "stale":
      return hasGeneratedLeads(summary) && summary.stale;
    case "has":
      return hasGeneratedLeads(summary) && !summary.stale;
    case "failed":
      return Boolean(summary.last_error);
    default:
      return true;
  }
}
